import { HashIcon, PlusIcon, XIcon } from '@icons/IconLibrary';
import { UIDiv, UIInput, UIText } from '@ui';
import { updateColumnValue } from '@utils/columnUtils.js';

export default class NumberCell extends UIDiv {
  constructor(
    column,
    item = {},
    value = '',
    canEdit = true,
    onValueChange = null,
    setCellEditingState = null,
  ) {
    super();

    this.column = column;
    this.item = item;
    this.value = value;
    this.isEditMode = false;
    this.canEdit = canEdit;
    this.onValueChange = onValueChange; // Callback for value changes instead of API calls
    this.setCellEditingState = setCellEditingState; // Reference to parent row for edit state notification
    this.render();
  }

  render() {
    this.clear();

    // If the cell is in edit mode, render the input
    if (!this.canEdit) {
      this.renderUneditableInput();
      return;
    }

    if (this.value.trim() === '') {
      this.renderEmptyState();
    } else {
      this.renderNumberState();
    }
  }

  renderEmptyState() {
    const emptyContainer = new UIDiv();
    emptyContainer.addClass(
      'group hover:border hover:border-solid hover:border-gray-300 border border-dashed border-border hover:bg-gray-50 rounded-md min-h-[34px] cursor-text',
    );

    // Hover state: show TypeIcon and plus
    const hoverContent = new UIDiv();
    hoverContent.addClass(
      'p-1 px-2 hidden group-hover:!flex items-center gap-1.5',
    );

    const calendarIcon = new HashIcon({
      size: 18,
      color: 'none',
    });
    const plusIcon = new PlusIcon({
      size: 12,
      className: 'bg-[var(--o-hive-primary-80)] rounded-full',
      strokeColor: 'white',
    });
    hoverContent.add(calendarIcon, plusIcon);
    emptyContainer.add(hoverContent);

    emptyContainer.setAttribute('tabindex', '0');
    emptyContainer.onKeyDown(event => {
      if (event.key === 'Enter' && !this.isEditMode) {
        event.preventDefault();
        this.enterEditMode();
      }
    });
    // Click handler to enter edit mode
    emptyContainer.onClick(() => {
      this.enterEditMode();
    });

    this.add(emptyContainer);
  }

  renderNumberState() {
    const numberContainer = new UIDiv();
    numberContainer.addClass(
      'group relative hover:border hover:border-gray-300 hover:bg-gray-50 rounded-md min-h-[34px] cursor-text truncate flex items-center gap-0.5',
    );

    const numberContent = new UIText(this.value);
    numberContent.addClass('truncate flex-1');
    numberContainer.add(numberContent);

    // Click handler to enter edit mode
    numberContainer.onClick(() => {
      this.enterEditMode();
    });
    numberContainer.setAttribute('tabindex', '0');
    numberContainer.onKeyDown(event => {
      if (event.key === 'Enter' && !this.isEditMode) {
        event.preventDefault();
        this.enterEditMode();
      }
    });

    // hover show clear icon on right
    const clearIcon = new XIcon({
      size: 12,
      className:
        'absolute right-2 top-1/2 transform -translate-y-1/2 hidden group-hover:!flex justify-center items-center bg-gray-200 hover:bg-gray-300 rounded-md cursor-pointer !w-5 !h-5',
    });
    clearIcon.onClick(async event => {
      event.stopPropagation(); // Prevent triggering the click handler
      try {
        // If onValueChange callback is provided, use it instead of API call
        if (this.onValueChange) {
          this.onValueChange('');
          // Update the local value
          this.value = '';
          this.exitEditMode();
        } else {
          // Update the value via API if we have the necessary information
          const entityType = !this.item.reconstructions ? 'folder' : 'file';
          const entityId = this.item.id || this.item.sharingId;

          await updateColumnValue(this.column.id, entityType, entityId, '');

          // Update the local value
          this.value = '';
          if (this.item) {
            this.item[this.column.key] = '';
          }
          this.exitEditMode();
        }
      } catch (error) {
        console.error('Failed to update column value:', error);
        toast.error(i18next.t('pages.projects.toast.failed_column_update'));
      }
    });
    numberContainer.add(clearIcon);

    this.add(numberContainer);
  }

  enterEditMode() {
    if (this.isEditMode) return;

    this.isEditMode = true;

    // Notify parent row that this cell is entering edit mode
    if (typeof this.setCellEditingState === 'function') {
      this.setCellEditingState(true);
    }

    this.clear();

    // Create input that fills the cell
    const input = new UIInput(this.value);
    input.setType('number');
    input.addClass(
      'w-full min-h-[34px] cursor-text rounded-md px-2 focus:outline-none focus:!ring-2 focus:!ring-[var(--o-hive-primary-80)]',
    );

    this.add(input);

    // Focus the input and select all text
    setTimeout(() => {
      input.dom.focus();
      if (this.value) {
        input.dom.select();
      }
    }, 0);

    let isEditingSaved = false;

    // Handle clicking outside the cell to exit edit mode
    const handleClickOutside = event => {
      if (isEditingSaved) return;

      // Check if the click is outside this cell
      if (!this.contains(event.target)) {
        document.removeEventListener('click', handleClickOutside);
        saveEdit(event);
      }
    };

    // Add the click outside listener with a slight delay to avoid immediate triggering
    setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);

    // Handle save on Enter or blur
    const saveEdit = async event => {
      if (isEditingSaved) return;
      isEditingSaved = true;

      // Clean up the click outside listener
      document.removeEventListener('click', handleClickOutside);

      const newValue = input.getValue().trim();
      if (newValue === this.value) {
        this.exitEditMode();
        return;
      }

      try {
        // If onValueChange callback is provided, use it instead of API call
        if (this.onValueChange) {
          this.onValueChange(newValue, event);
          // Update the local value
          this.value = newValue;
          this.exitEditMode();
        } else {
          // Update the value via API if we have the necessary information
          const entityType = !this.item.reconstructions ? 'folder' : 'file';
          const entityId = this.item.id || this.item.sharingId;

          await updateColumnValue(
            this.column.id,
            entityType,
            entityId,
            newValue,
          );

          // Update the local value
          this.value = newValue;
          if (this.item) {
            this.item[this.column.key] = newValue;
          }

          this.exitEditMode();
        }
      } catch (error) {
        toast.error(i18next.t('pages.projects.toast.failed_column_update'));
        console.error('Failed to update column value:', error);
        // Revert to original value on error
        this.exitEditMode();
      }
    };

    // Handle cancel on Escape
    const cancelEdit = event => {
      if (event.key === 'Escape') {
        if (isEditingSaved) return;
        isEditingSaved = true;
        this.exitEditMode();
      }
    };

    // Add event listeners
    input.onKeyDown(event => {
      if (event.key === 'Enter') {
        event.preventDefault();
        saveEdit(event);
      } else if (event.key === 'Escape') {
        cancelEdit(event);
      }
    });

    input.onBlur(saveEdit);
  }

  exitEditMode() {
    this.isEditMode = false;

    // Notify parent row that this cell is exiting edit mode
    if (typeof this.setCellEditingState === 'function') {
      this.setCellEditingState(false);
    }

    this.render();
  }

  renderUneditableInput() {
    const textContainer = new UIDiv();
    textContainer.addClass('min-h-[34px] flex items-center truncate');

    const textContent = new UIText(this.value);
    textContent.addClass('truncate flex-1');
    textContainer.add(textContent);

    this.add(textContainer);
  }
}
